//@ts-nocheck
import PlaceholderLogo from '../../assets/images/PlaceholderLogo';
import * as PropTypes from 'prop-types';
import './PassengerFieldsSecondRound.css';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Modal
} from '@mui/material';
import { EmbedSignDocument } from '@documenso/embed-react';
import { matchIsValidTel } from 'mui-tel-input';
import { CaretDown } from '@phosphor-icons/react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { parseISODateWithoutOffset } from '../../utils/dateUtility';
import { isEmailValid } from '../../utils/emailValidator';
import { useTranslation } from 'react-i18next';
import { format, add } from 'date-fns';
import {
  getPassengerFormFieldsRounds,
  generatePassengerSignedDocumentByPassengerId,
  savePassengerFormFieldsRounds
} from '../../services/booking/booking';
import { getTripSettings } from '../../services/tenant/tenant';
import { useParams } from 'react-router-dom';
import { getLocalTenantData } from '../../utils/localTenantData';
import { XGInput, XGButton } from 'xg-cl';
import globalConfig from '../../config/globalConfig';
import { useDocumentSocket } from '../../hooks/useDocumentSocket';
import SecondRounFormSkeleton from './SecondRounFormSkeleton';
import {
  DATE,
  TEXTAREA,
  CHECKBOX,
  RADIOBUTTON,
  PHONE,
  NUMBER,
  EMAIL,
  TEXTBOX
} from '../../constants/passengers';
import {
  getExistingPassengerFieldFiles,
  getPassengerFieldUploadMetadata,
  getPassengerSignableDocumentState,
  isFileUploadPassengerFieldType,
  isPassengerFieldVisibleForBooking,
  isSignableDocumentPassengerFieldType
} from '../../utils/passengerFieldTypes';
import { ErrorToast } from '../../utils/alerts';
import { mapSignedDocumentGenerateResponse } from '../../utils/passengerWaiverSigning';
import {
  parseTripLogoSetting,
  resolveOperatorLogo
} from '../../utils/operatorLogo';
import SecondRoundField from './SecondRoundField';
import SecondRoundSuccessfulMessage from './SecondRoundSuccessfulMessage/SecondRoundSuccessfulMessage';

const TRIP_LOGO_SETTINGCODE = 'TRIPLOGOURL';

const isBrowserFile = (file) =>
  typeof File !== 'undefined' && file instanceof File;

const SIGNED_VALUE = 'SIGNED';

const buildRoundFormMultipartPayload = (payload = {}, uploadEntries = []) => {
  const formData = new FormData();

  formData.append('payload', JSON.stringify(payload));

  uploadEntries.forEach(({ fileKey, file }) => {
    formData.append(`files[${fileKey}]`, file, file?.name ?? fileKey);
  });

  return formData;
};

const PassengerFieldsSecondRound = ({ id }) => {
  const [data, setData] = useState(null);
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [fieldUploadFiles, setFieldUploadFiles] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [didTripFetchSucceed, setDidTripFetchSucceed] = useState(true);
  const [signableDocumentModal, setSignableDocumentModal] = useState(null);
  const [signableDocumentOverrides, setSignableDocumentOverrides] = useState(
    {}
  );
  const [signingPassengerFieldId, setSigningPassengerFieldId] = useState(null);
  const { t } = useTranslation(['trip', 'common', 'errors', 'passengers']);
  const {
    enablePassengerFieldUploads,
    enablePaxWaivers,
    enableOperatorContactSettings
  } = useFlags();
  const svgString = encodeURIComponent(
    renderToStaticMarkup(<PlaceholderLogo />)
  );

  const { passengerKey } = useParams();
  const tenantData = getLocalTenantData();
  const globalTenantLogo = tenantData?.tenantlogo || tenantData?.logo || '';
  const svgLogoUrl = `data:image/svg+xml,${svgString}`;
  const [resolvedLogoUrl, setResolvedLogoUrl] = useState(globalTenantLogo);
  const tripDate = data?.trip?.startdate || null;
  const tenantLogo = resolvedLogoUrl || globalTenantLogo || null;
  const tripName = data?.trip?.tripname || null;
  const tripDuration = data?.trip?.duration || null;
  const startDate = parseISODateWithoutOffset(tripDate);
  const endDate =
    startDate && add(new Date(startDate), { days: tripDuration - 1 });
  const dateFormat = t('common:dateFormat');
  const atBookingPassengerFields = data?.passenger?.passengerfields || [];
  const firstNameField = atBookingPassengerFields.find(
    (field) => field.title === 'First Name'
  );
  const lastNameField = atBookingPassengerFields.find(
    (field) => field.title === 'Last Name'
  );

  const emailField = atBookingPassengerFields.find(
    (field) => field.title === 'Email Address'
  );

  //Sorted from newest to oldest based on sequence
  const visibleRounds =
    data?.collectionsRounds?.sort(
      (a, b) => a?.displayOrder - b?.displayOrder
    ) || [];

  //  Get existing passenger field values
  const getExistingPassengerField = (trippassengerfieldid) => {
    return data?.passenger?.passengerfields?.find(
      (field) => field.trippassengerfieldid === trippassengerfieldid
    );
  };

  const getExistingFieldValue = (trippassengerfieldid) => {
    const existingField = getExistingPassengerField(trippassengerfieldid);
    return existingField?.options?.[0]?.value || '';
  };

  const getFieldUploadFiles = (field) =>
    fieldUploadFiles[field?.trippassengerfieldid] ?? [];

  const getFieldExistingFiles = (field) => {
    const passengerField = data?.passenger?.passengerfields?.find(
      (existingField) =>
        existingField.trippassengerfieldid === field.trippassengerfieldid
    );

    return (
      getExistingPassengerFieldFiles(field) ??
      getExistingPassengerFieldFiles(passengerField) ??
      []
    );
  };

  const hasFileUploadValue = (field) =>
    getFieldExistingFiles(field).length > 0 ||
    getFieldUploadFiles(field).length > 0;

  // Get field configuration from fields library
  const getFieldConfig = (trippassengerfieldid) => {
    return data?.fieldsLibrary?.find(
      (field) => field.trippassengerfieldid === trippassengerfieldid
    );
  };

  const getSignableDocumentFieldValue = (field = {}) => {
    return (
      formValues[field?.trippassengerfieldid] ??
      field?.existingValue ??
      field?.existingField?.options?.[0]?.value ??
      ''
    );
  };

  const isSignedSignableDocumentField = (field = {}) => {
    const normalizedFieldValue = String(getSignableDocumentFieldValue(field))
      .trim()
      .toUpperCase();

    if (normalizedFieldValue === SIGNED_VALUE) {
      return true;
    }

    return (field?.existingOptions || field?.options || []).some(
      (option) =>
        String(option?.value || '')
          .trim()
          .toUpperCase() === SIGNED_VALUE
    );
  };

  //  Organize fields for display
  const organizedFields = useMemo(() => {
    if (!data?.fieldsLibrary) {
      return {
        currentRoundFields: [],
        previousRoundFields: [],
        filledFields: []
      };
    }

    const currentRoundFields = [];
    const previousRoundFields = [];
    const isPassengerFieldVisibleByFeatureFlags = (field) => {
      return isPassengerFieldVisibleForBooking(field, {
        enablePassengerFieldUploads,
        enablePaxWaivers
      });
    };
    const buildFieldData = (field, overrides = {}) => {
      const existingField = getExistingPassengerField(
        field.trippassengerfieldid
      );
      const existingValue = getExistingFieldValue(field.trippassengerfieldid);
      const existingFiles = getFieldExistingFiles(field);
      const existingOptions = existingField?.options ?? [];
      const fieldData = {
        ...field,
        ...overrides,
        existingField,
        existingFiles,
        existingOptions,
        existingValue
      };

      if (isSignableDocumentPassengerFieldType(field?.fieldtype)) {
        const signableDocumentState = getPassengerSignableDocumentState({
          ...fieldData,
          signedDocument:
            signableDocumentOverrides[field.trippassengerfieldid] ||
            fieldData.signedDocument
        });
        const isSigned = isSignedSignableDocumentField(fieldData);

        return {
          ...fieldData,
          signableDocumentState: {
            ...signableDocumentState,
            documentReady: signableDocumentState.documentReady || isSigned,
            documentready: signableDocumentState.documentReady || isSigned
          },
          isSigning: signingPassengerFieldId === field.trippassengerfieldid,
          hasValue: isSigned
        };
      }

      const hasExistingFiles =
        enablePassengerFieldUploads &&
        isFileUploadPassengerFieldType(field?.fieldtype) &&
        existingFiles.length > 0;

      return {
        ...fieldData,
        hasValue: Boolean(existingValue) || hasExistingFiles
      };
    };

    // Mirror the trip editor's at-booking section:
    // non-internal fields flagged as required/optional and not assigned to any collection round.
    const passengerFieldsNotInRounds =
      data?.fieldsLibrary?.filter((field) => {
        const isInRounds = visibleRounds.some((round) =>
          round.fields.some(
            (roundField) =>
              roundField.trippassengerfieldid === field.trippassengerfieldid
          )
        );
        const isAtBookingField =
          !field.isinternal &&
          isPassengerFieldVisibleByFeatureFlags(field) &&
          (field.isrequired || field.isoptional);

        const isExcludedField =
          field.title === 'First Name' ||
          field.title === 'Last Name' ||
          field.title === 'Email Address';

        return isAtBookingField && !isInRounds && !isExcludedField;
      }) || [];

    const filledFields = [];

    passengerFieldsNotInRounds.forEach((field) => {
      const fieldData = buildFieldData(field);

      if (fieldData.hasValue) {
        filledFields.push(fieldData);
      } else {
        currentRoundFields.push(fieldData);
      }
    });

    // Organize fields into categories
    visibleRounds?.forEach((round, roundIndex) => {
      round.fields.forEach((roundField) => {
        const fieldConfig = getFieldConfig(roundField.trippassengerfieldid);

        if (
          fieldConfig &&
          !fieldConfig?.isinternal &&
          isPassengerFieldVisibleByFeatureFlags(fieldConfig)
        ) {
          const fieldData = buildFieldData(fieldConfig, {
            assignmenttype: roundField.assignmenttype,
            sequence: roundField.sequence,
            roundName: round.name,
            roundSequence: round.sequence
          });

          if (fieldData.hasValue) {
            // Field has a value - goes to "Previously provided"
            filledFields.push(fieldData);
          } else {
            // Field has no value
            if (roundIndex === 0) {
              // Current round
              currentRoundFields.push(fieldData);
            } else {
              // Previous rounds with unfilled fields
              previousRoundFields.push(fieldData);
            }
          }
        }
      });
    });

    // Sort fields within each category
    currentRoundFields.sort((a, b) => a.sequence - b.sequence);
    previousRoundFields.sort((a, b) => {
      // First by round sequence, then by field sequence
      if (a.roundSequence !== b.roundSequence) {
        return a.roundSequence - b.roundSequence;
      }
      return a.sequence - b.sequence;
    });
    filledFields.sort((a, b) => a.sequence - b.sequence);

    return {
      currentRoundFields,
      previousRoundFields,
      filledFields
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    visibleRounds,
    enablePassengerFieldUploads,
    enablePaxWaivers,
    signableDocumentOverrides,
    signingPassengerFieldId
  ]);

  // Handle field value changes
  const handleFieldChange = (trippassengerfieldid, value) => {
    setFormValues((prev) => ({
      ...prev,
      [trippassengerfieldid]: value
    }));
  };

  const handleFieldFileChange = (trippassengerfieldid, files = []) => {
    setFieldUploadFiles((prev) => {
      const next = { ...prev };

      if (files.length) {
        next[trippassengerfieldid] = files;
      } else {
        delete next[trippassengerfieldid];
      }

      return next;
    });

    setFormValues((prev) => ({
      ...prev,
      [trippassengerfieldid]: files.map((file) => file?.name).join(', ')
    }));
  };

  const handleFieldFileError = (trippassengerfieldid, error = '') => {
    setFormErrors((prev) => ({
      ...prev,
      [trippassengerfieldid]: error || null
    }));
  };

  //  Get field value (from form state or existing value)
  const getFieldValue = (field) => {
    return formValues[field.trippassengerfieldid] ?? field.existingValue ?? '';
  };

  // Validate required fields
  const validateForm = () => {
    const allRequiredFields = [
      ...organizedFields.currentRoundFields,
      ...organizedFields.previousRoundFields,
      ...organizedFields.filledFields
    ].filter((field) => field.assignmenttype === 'required');

    for (const field of allRequiredFields) {
      const value = getFieldValue(field);
      const isFileUploadField =
        enablePassengerFieldUploads &&
        isFileUploadPassengerFieldType(field?.fieldtype);
      const isSignableDocumentField = isSignableDocumentPassengerFieldType(
        field?.fieldtype
      );

      if (isSignableDocumentField && !isSignedSignableDocumentField(field)) {
        return false;
      }

      if (isFileUploadField && !hasFileUploadValue(field)) {
        return false;
      }

      if (!isFileUploadField && (!value || !value.toString().trim())) {
        return false;
      }
    }
    return true;
  };

  const getFeedbackFromFields = (fields) => {
    const errors = [];

    for (const field of fields) {
      const { isrequired, fieldtype, title } = field;

      if (isSignableDocumentPassengerFieldType(fieldtype)) {
        const isRequired = isrequired || field.assignmenttype === 'required';

        if (isRequired && !isSignedSignableDocumentField(field)) {
          errors.push({
            ...field,
            error: t('passengers:pleaseSignPdf')
          });
        }
        continue;
      }

      const value = getFieldValue(field);
      const isFileUploadField =
        enablePassengerFieldUploads &&
        isFileUploadPassengerFieldType(fieldtype);
      const isRequired = isrequired || field.assignmenttype === 'required';

      // Check for required fields
      if (isRequired) {
        if (isFileUploadField && !hasFileUploadValue(field)) {
          errors.push({
            ...field,
            error: `${t('common:pleaseEnter')} ${title?.toLowerCase()}.`
          });
        } else if (
          (fieldtype?.key === CHECKBOX &&
            Array.isArray(value) &&
            value?.length === 0) ||
          (typeof value === 'string' && (!value || value?.trim() === '')) ||
          (!value && fieldtype?.key !== CHECKBOX && !isFileUploadField)
        ) {
          errors.push({
            ...field,
            error: `${t('common:pleaseEnter')} ${title?.toLowerCase()}.`
          });
        }
      }

      // Check for valid phone number
      if (
        !isFileUploadField &&
        fieldtype?.key === PHONE &&
        (isRequired || value?.trim().split(' ')?.length > 1)
      ) {
        const valid = matchIsValidTel(value?.trim());

        if (!valid) {
          errors.push({
            ...field,
            error: t('trip:pleaseEnterValidPhoneNumber')
          });
        }
      }

      // Check for valid email
      if (
        !isFileUploadField &&
        fieldtype?.key === EMAIL &&
        value?.trim() !== ''
      ) {
        if (!isEmailValid(value)) {
          errors.push({
            ...field,
            error: t('trip:pleaseEnterValidEmail')
          });
        }
      }

      // Check for valid number
      if (
        !isFileUploadField &&
        fieldtype?.key === NUMBER &&
        value?.trim() !== ''
      ) {
        if (isNaN(value?.trim())) {
          errors.push({
            ...field,
            error: t('trip:pleaseEnterValidNumber')
          });
        }
      }

      // Check for valid date
      if (
        !isFileUploadField &&
        fieldtype?.key === DATE &&
        (isRequired || value?.trim() !== '')
      ) {
        const parsedDate = Date.parse(value?.trim());

        if (isNaN(parsedDate)) {
          errors.push({
            ...field,
            error: t('trip:pleaseEnterValidDate')
          });
        }
      }
    }

    return errors;
  };

  const formatSubmissionPayload = (data, formValues, organizedFields) => {
    const uploadEntries = [];

    // Helper function to generate field key from title
    const generateFieldKey = (title) => {
      return title
        .toLowerCase()
        .replace(/[^a-zA-Z0-9]/g, '') // Remove special characters and spaces
        .trim();
    };

    // Helper function to find field option ID for a given value
    const findOptionId = (field, value) => {
      if (
        !field.trippassengerfieldoption ||
        field.trippassengerfieldoption.length === 0
      ) {
        return null;
      }

      const option = field.trippassengerfieldoption.find(
        (opt) => opt.title === value
      );
      return option ? option.trippassengerfieldoptionid : null;
    };

    const findOptionValueId = (field, value, existingOptions = []) => {
      if (!field.trippassengerfieldoption) {
        return null;
      }

      if (field?.trippassengerfieldoption?.length === 0) {
        return existingOptions?.[0]?.bookingpassengerfieldvalueid;
      } else {
        const option = field.trippassengerfieldoption.find(
          (opt) => opt.title === value
        );

        const foundValueId = existingOptions?.find(
          (opt) =>
            opt.trippassengerfieldoptionid ===
            option?.trippassengerfieldoptionid
        )?.bookingpassengerfieldvalueid;

        return foundValueId ? foundValueId : null;
      }
    };

    // Helper function to format field options based on field type
    const formatFieldOptions = (field, value, existingOptions = []) => {
      const fieldType = field.fieldtype?.key;

      switch (fieldType) {
        case CHECKBOX:
          if (!value) return [];
          // eslint-disable-next-line no-case-declarations
          const selectedValues = Array.isArray(value)
            ? value
            : value
                .split(',')
                .map((v) => v.trim())
                .filter((v) => v);

          return selectedValues.map((selectedValue) => {
            const optionId = findOptionId(field, selectedValue);
            return {
              bookingpassengerfieldvalueid: findOptionValueId(
                field,
                selectedValue,
                existingOptions
              ),
              trippassengerfieldoptionid: optionId,
              value: selectedValue
            };
          });

        case RADIOBUTTON:
          if (!value) return [];
          // eslint-disable-next-line no-case-declarations
          const optionId = findOptionId(field, value);
          return [
            {
              bookingpassengerfieldvalueid: findOptionValueId(
                field,
                value,
                existingOptions
              ),
              trippassengerfieldoptionid: optionId,
              value: value
            }
          ];

        case TEXTAREA:
        case TEXTBOX:
        case EMAIL:
        case PHONE:
        default:
          // Handle text inputs (no option ID needed)
          return value
            ? [
                {
                  bookingpassengerfieldvalueid: findOptionValueId(
                    field,
                    value,
                    existingOptions
                  ),
                  trippassengerfieldoptionid: null,
                  value: value.toString().trim()
                }
              ]
            : [];
      }
    };

    const formatFileUploadField = (field) => {
      const uploadedFiles = getFieldUploadFiles(field).filter(isBrowserFile);
      const files = uploadedFiles.map((file, index) => {
        const fileKey = `${data?.passenger?.bookingpassengerid}:${field.trippassengerfieldid}:${index + 1}`;

        uploadEntries.push({ fileKey, file });
        return getPassengerFieldUploadMetadata(file, fileKey, index + 1);
      });

      return {
        trippassengerfieldid: field.trippassengerfieldid,
        key: generateFieldKey(field.title),
        options: [],
        files
      };
    };

    // Group fields by rounds for the rounds array
    const roundsMap = new Map();

    // Process all form fields that belong to collection rounds
    [
      ...organizedFields.currentRoundFields,
      ...organizedFields.previousRoundFields,
      ...organizedFields.filledFields
    ].forEach((field) => {
      // Skip system fields as they go in atBookingFields
      if (field.issystemfield) return;
      if (isSignableDocumentPassengerFieldType(field?.fieldtype)) return;

      const value =
        formValues[field.trippassengerfieldid] || field.existingValue;
      const isFileUploadField =
        enablePassengerFieldUploads &&
        isFileUploadPassengerFieldType(field?.fieldtype);
      const fieldUploadFiles = getFieldUploadFiles(field);
      const existingOptions = data?.passenger?.passengerfields?.find(
        (pf) => pf.trippassengerfieldid === field.trippassengerfieldid
      )?.options;

      // Only include fields that have values. Existing uploaded file metadata is
      // shown in the form but only newly selected files are sent on submit.
      if (isFileUploadField ? fieldUploadFiles.length : value) {
        // Find which round this field belongs to
        const round = data?.collectionsRounds?.find((r) =>
          r.fields.some(
            (rf) => rf.trippassengerfieldid === field.trippassengerfieldid
          )
        );

        if (round) {
          if (!roundsMap.has(round.trippassengercollectionroundid)) {
            roundsMap.set(round.trippassengercollectionroundid, {
              trippassengercollectionroundid:
                round.trippassengercollectionroundid,
              name: round.name,
              fields: []
            });
          }

          const roundData = roundsMap.get(round.trippassengercollectionroundid);
          roundData.fields.push(
            isFileUploadField
              ? formatFileUploadField(field)
              : {
                  trippassengerfieldid: field.trippassengerfieldid,
                  key: generateFieldKey(field.title),
                  options: formatFieldOptions(field, value, existingOptions)
                }
          );
        }
      }
    });

    // Convert rounds map to array and sort by sequence
    const rounds = Array.from(roundsMap.values()).sort((a, b) => {
      const roundA = data?.collectionsRounds?.find(
        (r) =>
          r.trippassengercollectionroundid === a.trippassengercollectionroundid
      );
      const roundB = data?.collectionsRounds?.find(
        (r) =>
          r.trippassengercollectionroundid === b.trippassengercollectionroundid
      );
      return (roundA?.sequence || 0) - (roundB?.sequence || 0);
    });

    const nonSystemAtBookingFields =
      data?.fieldsLibrary?.filter(
        (field) =>
          !field.issystemfield &&
          !field.isinternal &&
          (enablePassengerFieldUploads ||
            !isFileUploadPassengerFieldType(field?.fieldtype)) &&
          (enablePaxWaivers ||
            !isSignableDocumentPassengerFieldType(field?.fieldtype)) &&
          (field.isrequired || field.isoptional)
      ) || [];

    const allRoundsFields = rounds?.reduce((acc, round) => {
      acc.push(...round.fields);
      return acc;
    }, []);

    // Get at-booking fields not present in rounds
    const fieldsNotInRounds = nonSystemAtBookingFields.filter(
      (field) =>
        !allRoundsFields.some(
          (roundField) =>
            roundField.trippassengerfieldid === field.trippassengerfieldid
        )
    );

    // Get all system/mandatory fields for atBookingFields
    const atBookingFields = [];
    const systemFields = ['First Name', 'Last Name', 'Email Address']; // Add other system fields as needed

    [
      ...(data?.fieldsLibrary?.filter((field) => field?.issystemfield) || []),
      ...fieldsNotInRounds
    ].forEach((field) => {
      if (field.issystemfield || systemFields.includes(field?.title)) {
        const existingOptions = data?.passenger?.passengerfields?.find(
          (pf) => pf.trippassengerfieldid === field.trippassengerfieldid
        )?.options;

        // Get value from existing data or form values
        const existingValue = existingOptions?.[0]?.value;

        const currentValue =
          formValues[field.trippassengerfieldid] || existingValue;

        if (currentValue) {
          atBookingFields.push({
            issystemfield: true,
            trippassengerfieldid: field.trippassengerfieldid,
            key: generateFieldKey(field.title),
            options: formatFieldOptions(field, currentValue, existingOptions)
          });
        }
      } else {
        const isFileUploadField =
          enablePassengerFieldUploads &&
          isFileUploadPassengerFieldType(field?.fieldtype);
        const existingOptions = data?.passenger?.passengerfields?.find(
          (pf) => pf.trippassengerfieldid === field.trippassengerfieldid
        )?.options;

        if (isFileUploadField) {
          const uploadedFiles = getFieldUploadFiles(field);

          if (!uploadedFiles.length) {
            return;
          }

          atBookingFields.push({
            issystemfield: false,
            ...formatFileUploadField(field)
          });
          return;
        }

        const options = formatFieldOptions(
          field,
          formValues[field?.trippassengerfieldid],
          existingOptions
        );

        atBookingFields.push({
          issystemfield: false,
          trippassengerfieldid: field.trippassengerfieldid,
          key: generateFieldKey(field.title),
          options: options || []
        });
      }
    });

    // Build final payload
    const payload = {
      tripId: data?.trip?.tripid || data?.booking?.tripid,
      bookingId: data?.booking?.bookingid,
      bookingPassengerId: data?.passenger?.bookingpassengerid,
      atBookingFields: atBookingFields.sort((a, b) => {
        // Sort by field sequence
        const fieldA = data?.fieldsLibrary?.find(
          (f) => f.trippassengerfieldid === a.trippassengerfieldid
        );
        const fieldB = data?.fieldsLibrary?.find(
          (f) => f.trippassengerfieldid === b.trippassengerfieldid
        );
        return (fieldA?.sequence || 0) - (fieldB?.sequence || 0);
      }),
      rounds: rounds
    };

    return { payload, uploadEntries };
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const errors = getFeedbackFromFields(
        [
          ...organizedFields.currentRoundFields,
          ...organizedFields.previousRoundFields,
          ...organizedFields.filledFields
        ] || []
      );
      if (errors.length > 0) {
        const newFormErrors = {};
        errors.forEach((error) => {
          newFormErrors[error.trippassengerfieldid] = error.error;
        });
        setFormErrors(newFormErrors);
        return;
      }

      const { payload: submissionPayload, uploadEntries } =
        formatSubmissionPayload(data, formValues, organizedFields);
      const requestPayload = uploadEntries.length
        ? buildRoundFormMultipartPayload(submissionPayload, uploadEntries)
        : submissionPayload;

      const { data: resData, error } = await savePassengerFormFieldsRounds(
        passengerKey,
        requestPayload
      );

      if (error || !resData) {
        ErrorToast(
          t('errors:submissionFailed', 'Submission failed. Please try again.')
        );
      } else {
        setShowSuccessMessage(true);
      }
    } catch (error) {
      ErrorToast(
        t('errors:submissionFailed', 'Submission failed. Please try again.')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setIsAccordionExpanded(isExpanded ? panel : false);
  };

  const getSimpleFieldValue = (field) => {
    return field?.options?.[0]?.value || '';
  };

  const fetchPassengerFieldsRounds = useCallback(async () => {
    if (enableOperatorContactSettings === undefined) return;

    try {
      setIsLoading(true);

      const res = await getPassengerFormFieldsRounds(passengerKey);

      if (res?.error) {
        setDidTripFetchSucceed(false);
        return;
      }

      const nextData = res?.data || null;
      const tripId = nextData?.trip?.tripid || nextData?.booking?.tripid;
      let tripLogoUrl = '';

      if (enableOperatorContactSettings && tripId) {
        try {
          const tripLogoSettings = await getTripSettings(
            tripId,
            TRIP_LOGO_SETTINGCODE
          );
          tripLogoUrl = parseTripLogoSetting(
            tripLogoSettings?.data?.settingvalue
          );
        } catch (error) {
          tripLogoUrl = '';
        }
      }

      setResolvedLogoUrl(
        resolveOperatorLogo({
          tripLogoUrl,
          globalLogoUrl: globalTenantLogo,
          enabled: enableOperatorContactSettings
        })
      );
      setData(nextData);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [enableOperatorContactSettings, globalTenantLogo, passengerKey]);

  const setSignableDocumentState = useCallback(
    (trippassengerfieldid, documentState = {}) => {
      setSignableDocumentOverrides((prev) => ({
        ...prev,
        [trippassengerfieldid]: {
          ...(prev[trippassengerfieldid] ?? {}),
          ...documentState
        }
      }));
    },
    []
  );

  const handleSignableDocumentCompleted = useCallback(() => {
    if (signableDocumentModal?.trippassengerfieldid) {
      setFormValues((prev) => ({
        ...prev,
        [signableDocumentModal.trippassengerfieldid]: SIGNED_VALUE
      }));
      setSignableDocumentState(signableDocumentModal.trippassengerfieldid, {
        documentReady: true,
        documentready: true,
        externalSignedDocumentId:
          signableDocumentModal.externalSignedDocumentId,
        externalsigneddocumentid:
          signableDocumentModal.externalSignedDocumentId,
        token: null
      });
    }

    setSignableDocumentModal(null);
    fetchPassengerFieldsRounds();
  }, [
    fetchPassengerFieldsRounds,
    setSignableDocumentState,
    signableDocumentModal
  ]);

  useDocumentSocket(
    signableDocumentModal?.externalSignedDocumentId,
    useCallback(
      (event) => {
        if (event === 'DOCUMENT_SIGNED') {
          handleSignableDocumentCompleted();
        }
      },
      [handleSignableDocumentCompleted]
    )
  );

  const handleSignableDocumentSign = async (field) => {
    const trippassengerfieldid = field?.trippassengerfieldid;
    const bookingpassengerid = data?.passenger?.bookingpassengerid;
    const email = getSimpleFieldValue(emailField)?.trim();
    const name = [
      getSimpleFieldValue(firstNameField),
      getSimpleFieldValue(lastNameField)
    ]
      .filter(Boolean)
      .join(' ')
      .trim();

    if (!bookingpassengerid || !trippassengerfieldid || !email || !name) {
      setFormErrors((prev) => ({
        ...prev,
        [trippassengerfieldid]: t('passengers:signerDetailsRequired')
      }));
      return;
    }

    setSigningPassengerFieldId(trippassengerfieldid);
    setFormErrors((prev) => ({
      ...prev,
      [trippassengerfieldid]: null
    }));

    try {
      const response = await generatePassengerSignedDocumentByPassengerId(
        bookingpassengerid,
        passengerKey,
        {
          trippassengerfieldid,
          email,
          name
        }
      );

      const {
        documentState,
        documentReady,
        token: documentToken,
        externalSignedDocumentId
      } = mapSignedDocumentGenerateResponse(response);

      if (response?.error || response?.data?.error || !documentState) {
        setFormErrors((prev) => ({
          ...prev,
          [trippassengerfieldid]: t(
            'errors:submissionFailed',
            'Submission failed. Please try again.'
          )
        }));
        return;
      }

      setSignableDocumentState(trippassengerfieldid, {
        ...documentState,
        documentReady,
        documentready: documentReady,
        externalSignedDocumentId,
        externalsigneddocumentid: externalSignedDocumentId,
        token: documentToken
      });

      if (documentReady) {
        setFormValues((prev) => ({
          ...prev,
          [trippassengerfieldid]: SIGNED_VALUE
        }));
        handleSignableDocumentCompleted();
        return;
      }

      // XGE-2848 idempotent signed-state responses can return an empty token.
      // Refreshing passenger form data keeps the waiver status in sync.
      if (!documentToken) {
        fetchPassengerFieldsRounds();
        return;
      }

      if (documentToken) {
        setSignableDocumentModal({
          trippassengerfieldid,
          token: documentToken,
          externalSignedDocumentId
        });
      }
    } catch (error) {
      const isNotFoundError =
        error?.toLocaleString()?.indexOf('NOT_FOUND') > -1;

      if (isNotFoundError) {
        setFormErrors((prev) => ({
          ...prev,
          [trippassengerfieldid]: t(
            'errors:signableDocumentNotFound',
            'Document not found. Please contact support.'
          )
        }));
        return;
      }
    } finally {
      setSigningPassengerFieldId(null);
    }
  };

  // Get passenger fields rounds data
  useEffect(() => {
    fetchPassengerFieldsRounds();
  }, [passengerKey, fetchPassengerFieldsRounds]);

  // Initialize form values with existing data
  useEffect(() => {
    if (data?.passenger?.passengerfields) {
      const initialValues = {};
      data?.passenger?.passengerfields?.forEach((field) => {
        const { options = [] } = field;
        initialValues[field.trippassengerfieldid] =
          options?.length > 1
            ? options.map((o) => o?.value)
            : options?.[0]?.value || '';
      });
      setFormValues(initialValues);
    }
  }, [data]);

  if (isLoading) {
    return <SecondRounFormSkeleton id='passenger-fields-skeleton' />;
  } else {
    return (
      <>
        {showSuccessMessage ? (
          <SecondRoundSuccessfulMessage
            id='second-round-success-message'
            data={data}
          />
        ) : (
          <Box
            id={id}
            data-testid='passenger-fields-second-round-output'
            className='passenger-fields-second-round-page__container'
          >
            <Box className='passenger-fields-second-round-page__content'>
              <Box className='passenger-fields-second-round-page__header'>
                <Box>
                  <img
                    src={tenantLogo ? tenantLogo : svgLogoUrl}
                    alt={data?.tenantName}
                    style={{ height: '40px' }}
                    draggable='false'
                  />
                </Box>
                <Box className='passenger-fields-second-round-page__header-title'>
                  <h1 className='text-xl-semibold'>
                    {tripName ? tripName : ''}{' '}
                  </h1>
                  {startDate && (
                    <Box
                      className='booking-modal-output-header__label'
                      style={{ display: 'flex' }}
                    >
                      <p className='text-sm-regular booking-modal-output-header__label-text'>
                        {`${format(new Date(startDate), dateFormat)} - ${
                          endDate && format(new Date(endDate), dateFormat)
                        }`}
                      </p>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Form Title Section */}
              <Box className='passenger-fields-second-round-page__form-title'>
                <Box className='passenger-fields-form-title'>
                  <Box className='passenger-fields-form-title__icon'>
                    <span className='passenger-fields-form-title__number'>
                      1
                    </span>
                  </Box>
                  <h2 className='passenger-fields-form-title__text'>
                    {t('common:passengerInformation', 'Passenger Information')}
                  </h2>
                </Box>
              </Box>

              <Box className='passenger-fields-second-round-page__form-mandatory'>
                <Box className='w-100 flex-left gap-12'>
                  <Box className='w-50 passenger-form__input-container' key={0}>
                    <XGInput
                      label={
                        <>
                          {t('common:firstName', 'First Name')}{' '}
                          <span className='passenger-fields-form__req-field'>
                            *
                          </span>
                        </>
                      }
                      name={'firstname'}
                      placeholder={`${t('common:enter')} first name`}
                      id={`passenger-firstname-input-${id}`}
                      type={'text'}
                      value={getSimpleFieldValue(firstNameField)}
                      onChange={() => () => {}}
                      disabled
                    />
                  </Box>

                  <Box className='w-50 passenger-form__input-container' key={0}>
                    <XGInput
                      label={
                        <>
                          {t('common:lastName', 'Last Name')}{' '}
                          <span className='passenger-fields-form__req-field'>
                            *
                          </span>
                        </>
                      }
                      name={'lastname'}
                      placeholder={`${t('common:enter')} last name`}
                      id={`passenger-firstname-input-${id}`}
                      type={'text'}
                      value={getSimpleFieldValue(lastNameField)}
                      onChange={() => () => {}}
                      disabled
                    />
                  </Box>
                </Box>
                <Box className='w-100 flex-left gap-12'>
                  <Box
                    className='w-100 passenger-form__input-container'
                    key={0}
                  >
                    <XGInput
                      label={
                        <>
                          {t('common:email', 'Email Address')}{' '}
                          <span className='passenger-fields-form__req-field'>
                            *
                          </span>
                        </>
                      }
                      name={'email'}
                      placeholder={`${t('common:enter')} email address`}
                      id={`passenger-email-input-${id}`}
                      type={'text'}
                      value={getSimpleFieldValue(emailField)}
                      onChange={() => () => {}}
                      disabled
                    />
                  </Box>
                </Box>
              </Box>

              {/* Current Round Fields */}
              {organizedFields.currentRoundFields.length > 0 && (
                <Box className='passenger-fields-second-round-page__form-current'>
                  {organizedFields.currentRoundFields.map((field) => (
                    <SecondRoundField
                      key={field.trippassengerfieldid}
                      id={`passenger-${field.trippassengerfieldid}-input-${id}  `}
                      formErrors={formErrors}
                      setFormErrors={setFormErrors}
                      handleFieldChange={handleFieldChange}
                      handleFieldFileChange={handleFieldFileChange}
                      handleFieldFileError={handleFieldFileError}
                      getFieldValue={getFieldValue}
                      getFieldFiles={getFieldUploadFiles}
                      field={field}
                      isSubmitting={isSubmitting}
                      onSignableDocumentSign={handleSignableDocumentSign}
                    />
                  ))}
                </Box>
              )}

              {/*  Previous Round Unfilled Fields */}
              {organizedFields.previousRoundFields.length > 0 && (
                <Box className='passenger-fields-second-round-page__form-previous'>
                  {organizedFields.previousRoundFields.map((field) => (
                    <SecondRoundField
                      key={field.trippassengerfieldid}
                      id={`passenger-${field.trippassengerfieldid}-input-${id}  `}
                      formErrors={formErrors}
                      setFormErrors={setFormErrors}
                      handleFieldChange={handleFieldChange}
                      handleFieldFileChange={handleFieldFileChange}
                      handleFieldFileError={handleFieldFileError}
                      getFieldValue={getFieldValue}
                      getFieldFiles={getFieldUploadFiles}
                      field={field}
                      isSubmitting={isSubmitting}
                      onSignableDocumentSign={handleSignableDocumentSign}
                    />
                  ))}
                </Box>
              )}

              {/* Previously Provided Section */}
              {organizedFields.filledFields.length > 0 && (
                <Box className='passenger-fields-second-round-page__previously-provided'>
                  <Accordion
                    expanded={isAccordionExpanded === 'previously-provided'}
                    onChange={handleAccordionChange('previously-provided')}
                    className='passenger-fields-previously-provided-accordion'
                    elevation={0}
                    disableGutters
                    disabled={isSubmitting}
                  >
                    <AccordionSummary
                      expandIcon={<CaretDown size={20} weight='regular' />}
                      aria-controls='previously-provided-content'
                      id='previously-provided-header'
                      className='passenger-fields-accordion-summary'
                    >
                      <Box className='passenger-fields-accordion-title'>
                        <span className='passenger-fields-accordion-title-text'>
                          {t(
                            'common:previouslyProvided',
                            'Previously provided'
                          )}
                        </span>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails className='passenger-fields-accordion-details'>
                      <Box className='passenger-fields-previously-provided-content'>
                        {organizedFields.filledFields.map((field) => (
                          <SecondRoundField
                            key={field.trippassengerfieldid}
                            id={`passenger-${field.trippassengerfieldid}-input-${id}  `}
                            formErrors={formErrors}
                            setFormErrors={setFormErrors}
                            handleFieldChange={handleFieldChange}
                            handleFieldFileChange={handleFieldFileChange}
                            handleFieldFileError={handleFieldFileError}
                            getFieldValue={getFieldValue}
                            getFieldFiles={getFieldUploadFiles}
                            field={field}
                            isSubmitting={isSubmitting}
                            onSignableDocumentSign={handleSignableDocumentSign}
                          />
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}

              <Box className='passenger-fields-second-round-page__footer'>
                <Box sx={{ position: 'relative' }}>
                  <XGButton
                    className='w-100'
                    type='primary'
                    size='medium'
                    onClick={handleSubmit}
                    variant='contained'
                    id='passenger-fields-submit-button'
                    text={t('common:submit', 'Submit')}
                    disabled={
                      isSubmitting ||
                      !validateForm() ||
                      !didTripFetchSucceed ||
                      !data
                    }
                  />
                  {isSubmitting && (
                    <CircularProgress
                      id='booking-modal-actions-loader'
                      size={24}
                      className='spinner spinner__primary'
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
        <Modal
          open={Boolean(signableDocumentModal?.token)}
          onClose={() => setSignableDocumentModal(null)}
          aria-labelledby='passenger-waiver-signing-modal'
        >
          <Box className='passenger-waiver-signing-modal'>
            {signableDocumentModal?.token && (
              <EmbedSignDocument
                host={globalConfig.eSignatureHost}
                token={signableDocumentModal.token}
                lockName
                onDocumentCompleted={handleSignableDocumentCompleted}
                className='passenger-waiver-signing-modal__embed'
              />
            )}
          </Box>
        </Modal>
      </>
    );
  }
};

PassengerFieldsSecondRound.propTypes = {
  id: PropTypes.string.isRequired
};

export default PassengerFieldsSecondRound;
